import React, { useState, useMemo, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { buildIngredientsDisplayData, isBeveragePsdRecipe, getIngredientMatchKey, isIngredientMatch } from "../data/ingredientsCalculations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useRecipeById } from "@/hooks/useRecipes";


export default function ScaleBatchColumn({
  scaleBatchVersion,
  comparedVersions = [],
  onSelectScaleBatchVersion,
  scaleBatchSize: propScaleBatchSize,
  onScaleBatchSizeChange,
  data,
  globalIngredients = [],
  showSolidLiquidColumn = true,
  formatDate = (d) => d || "-",
  isConfectionary = false,
  recipeFormat = "bakery",
  isBeveragePsd: propIsBeveragePsd,
  isMobile = false,
  tableRef,
  minHeight,
  ingredientRef,
  ingredientHeight,
  batchRef,
  sopRef,
  batchHeight,
  sopHeight,
  onSaveSpecificFields,
}) {
  const versionNumStr = String(Number(scaleBatchVersion?.version ?? 0) + 1).padStart(2, "0");

  const isCurrentData =
    scaleBatchVersion?._id === data?._id ||
    (scaleBatchVersion?.version !== undefined && data?.version !== undefined && scaleBatchVersion?.version === data?.version);
  const targetId = scaleBatchVersion?._id;

  const { data: fetchedDetail } = useRecipeById(targetId, {
    enabled: !isCurrentData && !!targetId && (!Array.isArray(scaleBatchVersion?.ingredients) || scaleBatchVersion.ingredients.length === 0),
  });

  const effectiveVersionData = useMemo(() => {
    if (isCurrentData && data) return data;
    if (fetchedDetail) return { ...scaleBatchVersion, ...fetchedDetail };
    return scaleBatchVersion || {};
  }, [isCurrentData, data, fetchedDetail, scaleBatchVersion]);

  const formatLabel = (
    effectiveVersionData?.recipeFormat ||
    effectiveVersionData?.recipeType ||
    recipeFormat ||
    data?.recipeFormat ||
    data?.recipeType ||
    "bakery"
  );
  const isBeveragePsd =
    propIsBeveragePsd !== undefined
      ? propIsBeveragePsd
      : isBeveragePsdRecipe(formatLabel);

  const effectiveStatus = effectiveVersionData?.recipeStatus || scaleBatchVersion?.recipeStatus;
  const vIsFinalized = ["final", "approved", "finalized"].includes(String(effectiveStatus || "").toLowerCase());

  const versionIngredients = useMemo(() => {
    if (Array.isArray(effectiveVersionData?.ingredients) && effectiveVersionData.ingredients.length > 0) {
      return effectiveVersionData.ingredients;
    }
    if (isCurrentData && Array.isArray(data?.ingredients) && data.ingredients.length > 0) {
      return data.ingredients;
    }
    return [];
  }, [effectiveVersionData?.ingredients, isCurrentData, data?.ingredients]);

  const normalizedVItem = useMemo(
    () => ({
      ...effectiveVersionData,
      recipeType: effectiveVersionData?.recipeType || data?.recipeType,
      ingredients: versionIngredients,
    }),
    [data?.recipeType, effectiveVersionData, versionIngredients]
  );

  // Batch Summary Edit States
  const [isBatchSummaryEditing, setIsBatchSummaryEditing] = useState(false);
  const [draftYield, setDraftYield] = useState(
    scaleBatchVersion?.yield ?? scaleBatchVersion?.outputYield ?? data?.yield ?? data?.outputYield ?? 100
  );
  const [draftServingSize, setDraftServingSize] = useState(
    scaleBatchVersion?.servingSize ?? scaleBatchVersion?.outputServingSize ?? data?.servingSize ?? data?.outputServingSize ?? 100
  );
  const [draftPerPiece, setDraftPerPiece] = useState(
    scaleBatchVersion?.perPiece ?? data?.perPiece ?? 12
  );
  const [draftPacketQuantity, setDraftPacketQuantity] = useState(
    scaleBatchVersion?.packetQuantity ?? data?.packetQuantity ?? 4
  );

  useEffect(() => {
    setDraftYield(
      scaleBatchVersion?.yield ?? scaleBatchVersion?.outputYield ?? data?.yield ?? data?.outputYield ?? 100
    );
    setDraftServingSize(
      scaleBatchVersion?.servingSize ?? scaleBatchVersion?.outputServingSize ?? data?.servingSize ?? data?.outputServingSize ?? 100
    );
    setDraftPerPiece(
      scaleBatchVersion?.perPiece ?? data?.perPiece ?? 12
    );
    setDraftPacketQuantity(
      scaleBatchVersion?.packetQuantity ?? data?.packetQuantity ?? 4
    );
  }, [
    scaleBatchVersion?.yield,
    scaleBatchVersion?.outputYield,
    data?.yield,
    data?.outputYield,
    scaleBatchVersion?.servingSize,
    scaleBatchVersion?.outputServingSize,
    data?.servingSize,
    data?.outputServingSize,
    scaleBatchVersion?.perPiece,
    data?.perPiece,
    scaleBatchVersion?.packetQuantity,
    data?.packetQuantity,
  ]);

  const vComputedData = useMemo(
    () =>
      buildIngredientsDisplayData(normalizedVItem, {
        recipeFormat,
        yield: draftYield,
        outputYield: draftYield,
        servingSize: draftServingSize,
        outputServingSize: draftServingSize,
        perPiece: draftPerPiece,
        packetQuantity: draftPacketQuantity,
      }),
    [normalizedVItem, recipeFormat, draftYield, draftServingSize, draftPerPiece, draftPacketQuantity]
  );

  const { ingredients: vIngredients, totals: vTotals, batchSummary: vBatchSummary } = vComputedData;

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const rawBatchSize =
    searchParams.get("batchSize") ||
    searchParams.get("batch_size") ||
    searchParams.get("sampleBatchSize") ||
    searchParams.get("sample_batch_size") ||
    searchParams.get("batch") ||
    location.state?.batchSize ||
    location.state?.batch_size ||
    location.state?.sampleBatchSize ||
    location.state?.sample_batch_size ||
    location.state?.batch ||
    null;

  const queryBatchSize = useMemo(() => {
    if (!rawBatchSize) return null;
    const cleaned = String(rawBatchSize).replace(/[^0-9.]/g, "").trim();
    return cleaned && !isNaN(Number(cleaned)) && Number(cleaned) > 0 ? cleaned : null;
  }, [rawBatchSize]);

  // Default total quantity from the base ingredients
  const defaultTotalQuantity = useMemo(() => {
    const sum = vIngredients.reduce((acc, curr) => acc + (Number(curr.quantity) || 0), 0);
    return sum > 0 ? sum : (Number(vTotals?.quantity) || 0);
  }, [vIngredients, vTotals?.quantity]);

  const [internalSampleBatchSize, setInternalSampleBatchSize] = useState(() => {
    if (queryBatchSize) {
      return String(queryBatchSize);
    }
    return "";
  });

  const sampleBatchSize =
    propScaleBatchSize !== undefined && propScaleBatchSize !== null
      ? String(propScaleBatchSize)
      : internalSampleBatchSize;

  const hasUserEditedRef = useRef(false);

  useEffect(() => {
    if (queryBatchSize && !hasUserEditedRef.current) {
      setInternalSampleBatchSize(String(queryBatchSize));
      onScaleBatchSizeChange?.(String(queryBatchSize));
      return;
    }
    if (!queryBatchSize && defaultTotalQuantity > 0 && (!sampleBatchSize || Number(sampleBatchSize) === 0) && !hasUserEditedRef.current) {
      setInternalSampleBatchSize(String(defaultTotalQuantity));
      if (propScaleBatchSize === undefined) {
        onScaleBatchSizeChange?.(String(defaultTotalQuantity));
      }
    }
  }, [defaultTotalQuantity, queryBatchSize, propScaleBatchSize]);

  // Reset batch size when version changes if no queryBatchSize was provided
  useEffect(() => {
    if (!queryBatchSize && propScaleBatchSize === undefined) {
      setInternalSampleBatchSize("");
      hasUserEditedRef.current = false;
    }
  }, [scaleBatchVersion?._id, scaleBatchVersion?.version, queryBatchSize, propScaleBatchSize]);

  const currentBatchSize = Number(sampleBatchSize) || defaultTotalQuantity || 0;
  const scaleFactor = defaultTotalQuantity > 0 && currentBatchSize > 0 ? currentBatchSize / defaultTotalQuantity : 1;

const formatIngredientCost = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : String(value);
};

  // Scale ingredient quantities & costs
  const displayIngredients = useMemo(() => {
    return vIngredients.map((item) => {
      const originalQty = Number(item.quantity) || 0;
      const scaledQty = originalQty * scaleFactor;
      const bffRate = Number(item.bffRate ?? item.bffRateAtCreation ?? 0);
      const clientRate = Number(item.clientRate ?? item.clientRateAtCreation ?? 0);
      const scaledBffCost = (bffRate * scaledQty) / 1000;
      const scaledClientCost = (clientRate * scaledQty) / 1000;
      return {
        ...item,
        scaledQuantity: scaledQty,
        displayQuantity: scaledQty % 1 === 0 ? String(scaledQty) : scaledQty.toFixed(2),
        bffRate,
        clientRate,
        bffCost: scaledBffCost,
        clientCost: scaledClientCost,
      };
    });
  }, [vIngredients, scaleFactor]);

  const totalQuantityDisplay = defaultTotalQuantity > 0 && currentBatchSize > 0
    ? (currentBatchSize % 1 === 0 ? String(currentBatchSize) : currentBatchSize.toFixed(2))
    : (vTotals?.quantity || "0");

  const effectiveMinHeight = minHeight || ingredientHeight;
  const effectiveTableRef = tableRef || ingredientRef;

  const displayIngredientsByKey = useMemo(() => {
    const map = new Map();
    (displayIngredients || []).forEach((ing) => {
      const key = getIngredientMatchKey(ing);
      if (key && !map.has(key)) {
        map.set(key, ing);
      }
    });
    return map;
  }, [displayIngredients]);

  const ingredientGroups = useMemo(() => {
    if (!globalIngredients || globalIngredients.length === 0) return [];

    const groups = [];
    let currentGroup = null;

    globalIngredients.forEach((item, index) => {
      const itemKey = getIngredientMatchKey(item);
      let matchedVIng = displayIngredientsByKey.get(itemKey);
      if (!matchedVIng) {
        matchedVIng = (displayIngredients || []).find((ing) => isIngredientMatch(ing, item));
      }
      const vIng = matchedVIng || {
        ...item,
        _id: undefined,
        quantity: null,
        displayQuantity: "-",
        composition: "-",
        perSachet: "-",
        finalDosing: "-",
        bffRate: null,
        clientRate: null,
        bffCost: null,
        clientCost: null,
        solidLiquid: null,
        isMissingInVersion: true,
        originalIndex: -1,
      };
      const typeKey = String(item.typeKey || item.type || "").toLowerCase();

      const prevItem = index > 0 ? globalIngredients[index - 1] : null;
      const prevTypeKey = prevItem ? String(prevItem.typeKey || prevItem.type || "").toLowerCase() : "";

      const shouldStartNewGroup =
        !currentGroup ||
        prevItem?.isSeparator ||
        (typeKey !== prevTypeKey &&
          (typeKey === "liquid" ||
            typeKey === "solid" ||
            prevTypeKey === "liquid" ||
            prevTypeKey === "solid"));

      if (shouldStartNewGroup) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = {
          items: [],
          isSeparator: false,
          solidLiquidValue: vIng.solidLiquid || item.solidLiquid || "",
        };
      }

      if ((vIng.solidLiquid || item.solidLiquid) && !currentGroup.solidLiquidValue) {
        currentGroup.solidLiquidValue = vIng.solidLiquid || item.solidLiquid;
      }
      if (item.isSeparator) {
        currentGroup.isSeparator = true;
      }

      currentGroup.items.push({ item, vIng, index });
    });

    if (currentGroup && currentGroup.items.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  }, [globalIngredients, displayIngredientsByKey, displayIngredients]);

  return (
    <div
      className={cn(
        "flex-none border-r border-[#EEEBF4] dark:border-primary/40 flex flex-col bg-white dark:bg-[#0D0B14] transition-all duration-200",
        isMobile
          ? "w-[480px]"
          : isBeveragePsd
            ? "w-[960px] lg:w-[520px] xl:w-[680px] 2xl:w-[820px] 3xl:w-[960px]"
            : "w-[880px] lg:w-[480px] xl:w-[620px] 2xl:w-[750px] 3xl:w-[880px]"
      )}
    >
      {/* INGREDIENT TABLE SECTION WITH SCALE BATCH HEADER */}
      <div
        ref={effectiveTableRef}
        style={effectiveMinHeight ? { minHeight: `${effectiveMinHeight}px` } : undefined}
        className="flex flex-col border-b border-[#EEEBF4] dark:border-primary/40 justify-between"
      >
        <div className="flex flex-col">
          {/* Version Header Card */}
          {isMobile ? (
            <>
              {/* 1. TOP VERSION CARD (Matching MobileVersionColumn h-[124px]) */}
              <div className="h-[124px] p-2.5 border-b border-[#EEEBF4] dark:border-primary/40 bg-white dark:bg-[#0D0B14]">
                <div className="h-full flex flex-col justify-between p-2.5 rounded-2xl bg-[#F7F5FA] dark:bg-primary/10 border border-[#EEEBF4] dark:border-primary/30">
                  {/* Top Row: Dropdown selector for version */}
                  <div className="flex items-center justify-between">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="px-2.5 py-1 rounded-xl bg-[#4B208B] hover:bg-[#3E1B77] text-white font-extrabold text-xs tracking-wider uppercase flex items-center gap-1 transition-all shadow-xs cursor-pointer"
                        >
                          <span>VERSION {versionNumStr}</span>
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="bg-white dark:bg-[#151221] border border-[#EEEBF4] dark:border-primary/40 shadow-lg rounded-xl p-1 min-w-[140px] z-50">
                        {comparedVersions.map((v) => {
                          const numStr = String(Number(v?.version ?? 0) + 1).padStart(2, "0");
                          const isSelected = (v._id || v.version) === (scaleBatchVersion?._id || scaleBatchVersion?.version);
                          return (
                            <DropdownMenuItem
                              key={v._id || v.version}
                              onClick={() => onSelectScaleBatchVersion?.(v)}
                              className={cn(
                                "px-3 py-2 text-xs font-bold rounded-lg cursor-pointer flex items-center justify-between transition-colors",
                                isSelected
                                  ? "bg-purple-100 dark:bg-primary/25 text-[#4B208B] dark:text-purple-300"
                                  : "text-gray-700 dark:text-gray-200 hover:bg-[#F7F5FA] dark:hover:bg-primary/10"
                              )}
                            >
                              <span>VERSION {numStr}</span>
                              {isSelected && <Check className="w-3.5 h-3.5 text-[#4B208B] dark:text-purple-300" />}
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Dates & Code */}
                  <div className="text-[10px] leading-tight space-y-0.5">
                    <p className="text-gray-500 dark:text-gray-400">
                      Code:{" "}
                      <span className="font-bold text-gray-800 dark:text-gray-200">
                        {scaleBatchVersion?.recipeCode || data?.recipeCode || "-"}
                      </span>
                    </p>
                    <p className="text-gray-500 dark:text-gray-400">
                      Created:{" "}
                      <span className="font-bold text-gray-800 dark:text-gray-200">
                        {formatDate(scaleBatchVersion?.createdAt || data?.createdAt) || "-"}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* 2. SCALE BATCH TITLE HEADER ROW (Matching Mobile 56px action/download row height) */}
              <div className="h-14 px-3 flex items-center justify-center border-b border-[#EEEBF4] dark:border-primary/40 bg-white dark:bg-[#0D0B14]">
                <h3 className="text-xs font-extrabold text-[#4B208B] dark:text-purple-300 tracking-wider uppercase">
                  Scale Batch
                </h3>
              </div>
            </>
          ) : (
            <div
              className={cn(
                "p-3 lg:p-1.5 xl:p-2 2xl:p-2.5 3xl:p-3 flex flex-col justify-between border-b border-[#EEEBF4] dark:border-primary/40 bg-white dark:bg-[#0D0B14]",
                isBeveragePsd
                  ? "h-[224px] lg:h-[120px] xl:h-[155px] 2xl:h-[180px] 3xl:h-[224px]"
                  : "h-[148px] lg:h-[80px] xl:h-[105px] 2xl:h-[120px] 3xl:h-[148px]"
              )}
            >
              <div className="flex flex-col justify-between p-3 lg:p-1.5 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-2xl lg:rounded-lg xl:rounded-xl 2xl:rounded-xl 3xl:rounded-2xl bg-[#F7F5FA] dark:bg-primary/10 border border-[#EEEBF4] dark:border-primary/30">
                {/* Top Row: Dropdown selector for version */}
                <div className="flex items-center justify-between">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="px-3 lg:px-1.5 xl:px-2 2xl:px-2.5 3xl:px-3 py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 3xl:py-1 rounded-xl lg:rounded-md xl:rounded-lg 2xl:rounded-lg 3xl:rounded-xl bg-[#4B208B] hover:bg-[#3E1B77] text-white font-extrabold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs tracking-wider uppercase flex items-center gap-1.5 lg:gap-1 transition-all shadow-sm cursor-pointer"
                      >
                        <span>VERSION {versionNumStr}</span>
                        <ChevronDown className="w-3.5 lg:w-2 xl:w-2.5 2xl:w-3 3xl:w-3.5 h-3.5 lg:h-2 xl:h-2.5 2xl:h-3 3xl:h-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="bg-white dark:bg-[#151221] border border-[#EEEBF4] dark:border-primary/40 shadow-lg rounded-xl p-1 min-w-[140px] z-50">
                      {comparedVersions.map((v) => {
                        const numStr = String(Number(v?.version ?? 0) + 1).padStart(2, "0");
                        const isSelected = (v._id || v.version) === (scaleBatchVersion?._id || scaleBatchVersion?.version);
                        return (
                          <DropdownMenuItem
                            key={v._id || v.version}
                            onClick={() => onSelectScaleBatchVersion?.(v)}
                            className={cn(
                              "px-3 py-2 text-xs font-bold rounded-lg cursor-pointer flex items-center justify-between transition-colors",
                              isSelected
                                ? "bg-purple-100 dark:bg-primary/25 text-[#4B208B] dark:text-purple-300"
                                : "text-gray-700 dark:text-gray-200 hover:bg-[#F7F5FA] dark:hover:bg-primary/10"
                            )}
                          >
                            <span>VERSION {numStr}</span>
                            {isSelected && <Check className="w-3.5 h-3.5 text-[#4B208B] dark:text-purple-300" />}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Middle: Dates and Code */}
                <div className="text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px] leading-tight space-y-0.5 mt-2 lg:mt-1 xl:mt-1.5 2xl:mt-1.5 3xl:mt-2">
                  <p className="text-gray-500 font-medium">
                    Recipe Creation Date:{" "}
                    <span className="font-bold text-gray-800 dark:text-gray-200">
                      {formatDate(scaleBatchVersion?.createdAt || data?.createdAt) || "-"}
                    </span>
                  </p>
                  <p className="text-gray-500 font-medium">
                    Recipe Code:{" "}
                    <span className="font-bold text-gray-800 dark:text-gray-200">
                      {scaleBatchVersion?.recipeCode || data?.recipeCode || "-"}
                    </span>
                    {vIsFinalized && (
                      <span className="ml-3 text-gray-500 font-medium">
                        In Development Code:{" "}
                        <span className="font-bold text-gray-800 dark:text-gray-200">
                          {scaleBatchVersion?.inDevelopmentCode ||
                            data?.inDevelopmentCode ||
                            scaleBatchVersion?.referenceCode ||
                            data?.referenceCode ||
                            "N/A"}
                        </span>
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Scale Batch Section Header */}
              <div className="flex items-center h-[34px] lg:h-[20px] xl:h-[26px] 2xl:h-[30px] 3xl:h-[34px] px-1">
                <h3 className="text-lg lg:text-[10px] xl:text-xs 2xl:text-sm 3xl:text-lg font-bold text-gray-900 dark:text-white tracking-tight">
                  Scale Batch
                </h3>
              </div>
            </div>
          )}

          {/* Table Columns Header */}
          <div className="flex items-center h-11 lg:h-6 xl:h-7.5 2xl:h-8.5 3xl:h-11 border-b border-[#EEEBF4] dark:border-primary/40 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-900 dark:text-white bg-white dark:bg-[#0D0B14]">
            {isBeveragePsd ? (
              <>
                <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  Per Sachet (g/pcs)
                </div>
                <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  Qty (g)
                </div>
                <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  Cmp (%)
                </div>
                <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  Final Dosing (%)
                </div>
                <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  BFF Rate (৳)
                </div>
                <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  Client Rate (৳)
                </div>
                <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  BFF Cost (৳)
                </div>
                <div className="flex-1 h-full flex items-center justify-center whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  Client Cost (৳)
                </div>
              </>
            ) : (
              <>
                <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  Qty (g)
                </div>
                <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  Cmp (%)
                </div>
                <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  BFF Rate (৳)
                </div>
                <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  Client Rate (৳)
                </div>
                <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                  BFF Cost (৳)
                </div>
                <div
                  className={cn(
                    "flex-1 h-full flex items-center justify-center whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]",
                    showSolidLiquidColumn && "border-r border-[#EEEBF4] dark:border-primary/30"
                  )}
                >
                  Client Cost (৳)
                </div>
                {showSolidLiquidColumn && (
                  <div className="flex-1 h-full flex items-center justify-center whitespace-nowrap px-1 text-[11px] lg:text-[6px] xl:text-[7.5px] 2xl:text-[8.5px] 3xl:text-[11px]">
                    S/L (%)
                  </div>
                )}
              </>
            )}
          </div>

          {/* Table Data Rows */}
          <div className="flex flex-col">
            {ingredientGroups.map((group, gIdx) => (
              <div
                key={gIdx}
                className="flex"
              >
                {isBeveragePsd ? (
                  <div className="flex-1 flex flex-col">
                    {group.items.map(({ vIng, index }, itemIdx) => {
                      const isLastInGroup = itemIdx === group.items.length - 1;
                      const isSeparator = group.isSeparator && isLastInGroup;
                      const isMissing = Boolean(vIng.isMissingInVersion);
                      return (
                        <div
                          key={index}
                          className={cn(
                            "flex items-center h-12 lg:h-6.5 xl:h-8 2xl:h-9.5 3xl:h-12 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-[#F7F5FA] dark:hover:bg-primary/10 transition-colors",
                            isSeparator
                              ? "border-b-2 border-[#4B208B]"
                              : "border-b border-[#EEEBF4] dark:border-primary/30"
                          )}
                        >
                          <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                            <span className="truncate">{isMissing ? "-" : (vIng.perSachet ?? "-")}</span>
                          </div>
                          <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                            <span className="truncate">{isMissing ? "-" : (vIng.displayQuantity ?? vIng.quantity ?? "-")}</span>
                          </div>
                          <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                            <span className="truncate">{isMissing ? "-" : (vIng.composition ?? "-")}</span>
                          </div>
                          <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                            <span className="truncate">{isMissing ? "-" : (vIng.finalDosing ?? "-")}</span>
                          </div>
                          <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                            <span className="truncate">{isMissing ? "-" : formatIngredientCost(vIng.bffRate)}</span>
                          </div>
                          <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                            <span className="truncate">{isMissing ? "-" : formatIngredientCost(vIng.clientRate)}</span>
                          </div>
                          <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                            <span className="truncate">{isMissing ? "-" : formatIngredientCost(vIng.bffCost)}</span>
                          </div>
                          <div className="flex-1 h-full flex items-center justify-center px-1">
                            <span className="truncate">{isMissing ? "-" : formatIngredientCost(vIng.clientCost)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    className={cn(
                      "flex flex-col",
                      showSolidLiquidColumn
                        ? "flex-[6] border-r border-[#EEEBF4] dark:border-primary/30"
                        : "flex-1"
                    )}
                  >
                    {group.items.map(({ vIng, index }, itemIdx) => {
                      const isLastInGroup = itemIdx === group.items.length - 1;
                      const isSeparator = group.isSeparator && isLastInGroup;
                      const isMissing = Boolean(vIng.isMissingInVersion);
                      return (
                        <div
                          key={index}
                          className={cn(
                            "flex items-center h-12 lg:h-6.5 xl:h-8 2xl:h-9.5 3xl:h-12 text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-medium text-gray-800 dark:text-gray-200 hover:bg-[#F7F5FA] dark:hover:bg-primary/10 transition-colors",
                            isSeparator
                              ? "border-b-2 border-[#4B208B]"
                              : "border-b border-[#EEEBF4] dark:border-primary/30"
                          )}
                        >
                          <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                            <span className="truncate">{isMissing ? "-" : (vIng.displayQuantity ?? vIng.quantity ?? "-")}</span>
                          </div>
                          <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                            <span className="truncate">{isMissing ? "-" : (vIng.composition ?? "-")}</span>
                          </div>
                          <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                            <span className="truncate">{isMissing ? "-" : formatIngredientCost(vIng.bffRate)}</span>
                          </div>
                          <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                            <span className="truncate">{isMissing ? "-" : formatIngredientCost(vIng.clientRate)}</span>
                          </div>
                          <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                            <span className="truncate">{isMissing ? "-" : formatIngredientCost(vIng.bffCost)}</span>
                          </div>
                          <div
                            className={cn(
                              "flex-1 h-full flex items-center justify-center px-1",
                              showSolidLiquidColumn && "border-r border-[#EEEBF4] dark:border-primary/30"
                            )}
                          >
                            <span className="truncate">{isMissing ? "-" : formatIngredientCost(vIng.clientCost)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!isBeveragePsd && showSolidLiquidColumn && (
                  <div
                    className={cn(
                      "flex-1 flex items-center justify-center text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-900 dark:text-white bg-white dark:bg-[#0D0B14]",
                      group.isSeparator
                        ? "border-b-2 border-[#4B208B]"
                        : "border-b border-[#EEEBF4] dark:border-primary/30"
                    )}
                  >
                    <span>{group.solidLiquidValue || ""}</span>
                  </div>
                )}
              </div>
            ))}

            {/* Total Row */}
            <div className="flex items-center h-12 lg:h-6.5 xl:h-8 2xl:h-9.5 3xl:h-12 font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-[#4B208B] dark:text-purple-300 border-t-2 border-[#4B208B]/40 bg-[#FCFBFD] dark:bg-[#121019]">
              {isBeveragePsd ? (
                <>
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                    {vTotals?.perSachet ?? "-"}
                  </div>
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                    {totalQuantityDisplay}
                  </div>
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                    {vTotals?.composition}
                  </div>
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                    {vTotals?.finalDosing ?? "-"}
                  </div>
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">-</div>
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">-</div>
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                    <span className="truncate">{formatIngredientCost(Number(vTotals?.bffCost || 0) * scaleFactor)}</span>
                  </div>
                  <div className="flex-1 h-full flex items-center justify-center px-1">
                    <span className="truncate">{formatIngredientCost(Number(vTotals?.clientCost || 0) * scaleFactor)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                    {totalQuantityDisplay}
                  </div>
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                    {vTotals?.composition}
                  </div>
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">-</div>
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">-</div>
                  <div className="flex-1 h-full flex items-center justify-center border-r border-[#EEEBF4] dark:border-primary/30 px-1">
                    <span className="truncate">{formatIngredientCost(Number(vTotals?.bffCost || 0) * scaleFactor)}</span>
                  </div>
                  <div
                    className={cn(
                      "flex-1 h-full flex items-center justify-center px-1",
                      showSolidLiquidColumn && "border-r border-[#EEEBF4] dark:border-primary/30"
                    )}
                  >
                    <span className="truncate">{formatIngredientCost(Number(vTotals?.clientCost || 0) * scaleFactor)}</span>
                  </div>
                  {showSolidLiquidColumn && (
                    <div className="flex-1 h-full flex items-center justify-center px-1">
                      {vTotals?.solidLiquid}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Sample Batch Size Input */}
        <div
          className={cn(
            "bg-white dark:bg-[#0D0B14] flex flex-col justify-center",
            isMobile ? "p-3 min-h-[96px]" : "p-4 lg:p-2 xl:p-2.5 2xl:p-3 3xl:p-4 min-h-[96px] lg:min-h-[50px] xl:min-h-[66px] 2xl:min-h-[80px] 3xl:min-h-[96px]"
          )}
        >
          <h4 className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-bold text-gray-900 dark:text-white mb-1.5 lg:mb-0.5">
            Sample Batch Size
          </h4>
          <div className="w-full h-9 lg:h-5 xl:h-6 2xl:h-7.5 3xl:h-9 px-3 lg:px-1.5 xl:px-2 2xl:px-2.5 3xl:px-3 rounded-xl lg:rounded-md bg-[#EFEAF9]/60 dark:bg-primary/10 border border-purple-200/60 dark:border-primary/30 focus-within:border-[#4B208B] flex items-center justify-between shadow-xs transition-colors">
            <input
              type="number"
              value={sampleBatchSize}
              onChange={(e) => {
                hasUserEditedRef.current = true;
                setInternalSampleBatchSize(e.target.value);
                onScaleBatchSizeChange?.(e.target.value);
              }}
              placeholder="0"
              className="w-full bg-transparent border-none outline-none font-bold text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-gray-900 dark:text-white"
            />
            <span className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs font-semibold text-gray-500 ml-1">g</span>
          </div>
          {!isMobile && (
            <p className="text-xs lg:text-[6.5px] xl:text-[8.5px] 2xl:text-[9.5px] 3xl:text-xs text-gray-400 dark:text-gray-500 mt-1.5 lg:mt-0.5">
              Adjust the sample batch size to preview updated quantities.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

